/**
 * stageWriteBarrier — lets a chat retry wait for an outstanding stage write.
 *
 * WHY
 * ---
 * A stage jump applies to the view IMMEDIATELY (React state) and persists
 * best-effort afterwards, so there is a window where the student is looking at
 * stage N while the DB still says N-1. A chat sent in that window is correctly
 * answered `stale`, granting exactly one retry — but if the retry beats the
 * outstanding `/set-stage` POST, the server sees the SAME mismatch again and
 * ends the turn `neutral_final`. The student spends their only retry and never
 * gets an answer, having done nothing wrong.
 *
 * The fix is to wait for the write, not to hand out more retries: more retries
 * would just lose the race more times.
 *
 * WHY A MODULE, NOT A PROP
 * ------------------------
 * The writer (`persistStageJump` in InteractiveGraph) and the waiter (the retry
 * branch in ChatInterface) are siblings. Threading a promise between them would
 * mean new props through Dashboard and ProblemIntegrationView, both of which
 * already forward render state; this keeps that plumbing unchanged.
 *
 * ADVISORY, NEVER BLOCKING
 * ------------------------
 * The wait is bounded and always resolves. If a write hangs or a count is left
 * stranded by an unmounted component, the worst case is a bounded delay before
 * the retry goes out anyway — never a chat that stops responding.
 */

const DEFAULT_TIMEOUT_MS = 3000;

let inFlight = 0;
let waiters = [];

function drain() {
  const pending = waiters;
  waiters = [];
  pending.forEach((resolve) => resolve());
}

/** Call when a stage write starts. */
export function beginStageWrite() {
  inFlight += 1;
}

/** Call when a stage write settles — success or failure, both count. */
export function endStageWrite() {
  inFlight = Math.max(0, inFlight - 1);
  if (inFlight === 0) drain();
}

/**
 * Resolve once no stage write is outstanding, or after `timeoutMs`.
 *
 * Resolves immediately when nothing is in flight, which is the common case —
 * this costs a microtask, not a delay, on the ordinary path.
 */
export function waitForStageWrites(timeoutMs = DEFAULT_TIMEOUT_MS) {
  if (inFlight === 0) return Promise.resolve();
  return new Promise((resolve) => {
    let settled = false;
    let timer = null;
    const finish = () => {
      if (settled) return;
      settled = true;
      // Release BOTH sides. Leaving the timeout pending kept a 3s (or longer)
      // handle alive for every wait, which is what a test suite sees as a
      // process that will not exit.
      if (timer !== null) clearTimeout(timer);
      waiters = waiters.filter((w) => w !== finish);
      resolve();
    };
    waiters.push(finish);
    timer = setTimeout(finish, timeoutMs);
  });
}

/** Test helper: drop all state so suites do not leak into each other. */
export function __resetStageWriteBarrier() {
  inFlight = 0;
  waiters = [];
}
